// prediction/PredictionSection.jsx
import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  IconButton,
  Tooltip,
  Grid
} from '@mui/material';
import UpdateIcon from '@mui/icons-material/Update';
import SettingsIcon from '@mui/icons-material/Settings';
import TankIcon from '@mui/icons-material/Water';
import NotificationsIcon from '@mui/icons-material/Notifications';
import OpacityIcon from '@mui/icons-material/Opacity';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import LocalGasStationIcon from '@mui/icons-material/LocalGasStation';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TimelineIcon from '@mui/icons-material/Timeline';

/**
 * 予測・タンク情報セクションコンポーネント
 * 🚀 補充タイミング表示制御を追加
 */
const PredictionSection = ({ 
  selectedProduct,
  serviceMode,
  isTankFunctionEnabled,
  isPredictionFunctionEnabled,
  onUpdateClick,
  onEditClick,
  onFunctionControlClick
}) => {

  // ユーティリティ関数
  const formatDate = (dateString) => {
    if (!dateString) return "未設定";
    return new Date(dateString).toLocaleDateString();
  };

  const isRecentDate = (dateString) => {
    if (!dateString) return false;
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    return diffDays < 7;
  };

  const getDaysSince = (dateString) => {
    if (!dateString) return 0;
    const date = new Date(dateString);
    const now = new Date();
    return Math.floor((now - date) / (1000 * 60 * 60 * 24));
  };

  const isBelowThreshold = (product) => {
    if (!product || !product.thresholdQuantity || !product.predictedQuantity) {
      return false;
    }
    return product.predictedQuantity <= product.thresholdQuantity;
  };

  const getRemainingColor = (ratio) => {
    if (ratio === null || ratio === undefined) return "#9e9e9e";
    if (ratio <= 20) return "#f44336";
    if (ratio <= 40) return "#ff9800";
    return "#4caf50";
  };

  // 容量設定状況の判定
  const hasCapacity = selectedProduct.totalCapacity != null;
  const hasPrediction = selectedProduct.predictedQuantity != null;
  
  // 残量低下の判定（容量設定時のみ）
  const isLowRemaining = hasCapacity && selectedProduct.predictedRatio != null && selectedProduct.predictedRatio <= 20;
  
  // 実測から長期間経過の判定
  const daysSinceLastMeasurement = getDaysSince(selectedProduct.measuredDate);
  const isOldMeasurement = selectedProduct.measuredDate && daysSinceLastMeasurement > 7;

  // サービスモード別ボタン表示制御
  const getButtonConfig = () => {
    switch (serviceMode) {
      case 'PRICING_ONLY':
        return {
          showEditButton: false,
          showUpdateButton: false,
          title: '機能案内'
        };
      case 'TANK_MANUAL':
        return {
          showEditButton: true,
          showUpdateButton: false,
          editLabel: 'タンク設定',
          editTooltip: 'タンク容量を編集',
          title: 'タンク情報'
        };
      case 'PREDICTION_ONLY':
        return {
          showEditButton: true,
          showUpdateButton: true,
          editLabel: '予測設定',
          editTooltip: '消費量設定を編集',
          updateLabel: '残量更新',
          updateTooltip: '実測残量を更新',
          title: '残量管理'
        };
      case 'PREDICTION_AUTO':
        return {
          showEditButton: true,
          showUpdateButton: true,
          editLabel: '製品設定',
          editTooltip: '製品設定を編集',
          updateLabel: '残量更新',
          updateTooltip: '実測残量を更新',
          title: 'タンク情報'
        };
      default:
        return {
          showEditButton: false,
          showUpdateButton: false,
          title: '情報'
        };
    }
  };

  const buttonConfig = getButtonConfig();

  // ヘッダー部分のボタン表示制御
  const renderHeaderButtons = () => {
    return (
      <Box display="flex" gap={1}>
        {buttonConfig.showUpdateButton && (
          <Tooltip title={buttonConfig.updateTooltip}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<UpdateIcon />}
              onClick={onUpdateClick}
            >
              {buttonConfig.updateLabel}
            </Button>
          </Tooltip>
        )}

        {buttonConfig.showEditButton ? (
          <Tooltip title={buttonConfig.editTooltip}>
            <IconButton 
              size="small" 
              color="primary"
              onClick={onEditClick}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>
    );
  };

  // 予測残量表示の判定ロジック
  const renderRemainingDisplay = () => {
    if (!isPredictionFunctionEnabled) {
      return null;
    }

    if (!hasPrediction) {
      return (
        <Box>
          <Typography variant="h6" fontWeight="bold" color="text.secondary" sx={{ mb: 1 }}>
            未計算
          </Typography>
          <Chip 
            label="データ不足" 
            size="small" 
            color="warning" 
            variant="outlined"
          />
        </Box>
      );
    }
    
    if (!isTankFunctionEnabled) {
      return (
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            {selectedProduct.predictedQuantity}L
          </Typography>
          <Typography 
            variant="caption" 
            color="primary" 
            sx={{ 
              display: 'block', 
              mt: 1, 
              cursor: 'pointer',
              textDecoration: 'underline'
            }}
            onClick={onFunctionControlClick}
          >
            タンク機能を有効にする
          </Typography>
        </Box>
      );
    }

    if (!hasCapacity) {
      return (
        <Box>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
            {selectedProduct.predictedQuantity}L
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            <Chip 
              label="容量未設定" 
              size="small" 
              color="info" 
              variant="outlined"
              icon={<SettingsIcon sx={{ fontSize: '14px !important' }} />}
            />
            <Tooltip title="タンク容量を設定すると割合表示と詳細な予測機能が利用できます">
              <InfoIcon sx={{ fontSize: 16, color: 'text.secondary', cursor: 'help' }} />
            </Tooltip>
          </Box>
          {onEditClick && buttonConfig.showEditButton && (
            <Typography 
              variant="caption" 
              color="primary" 
              sx={{ 
                display: 'block', 
                mt: 1, 
                cursor: 'pointer',
                textDecoration: 'underline'
              }}
              onClick={onEditClick}
            >
              容量を設定する
            </Typography>
          )}
        </Box>
      );
    }
    
    return (
      <Box>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
          {selectedProduct.predictedQuantity}L ({selectedProduct.predictedRatio?.toFixed(1) ?? "?"}%)
        </Typography>
        
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 0.5,
          alignItems: 'center'
        }}>
          {isLowRemaining && (
            <Chip 
              icon={<WarningIcon sx={{ fontSize: '14px !important' }} />} 
              label="残量低下" 
              color="error" 
              size="small" 
              sx={{ 
                height: 24,
                fontSize: '0.75rem',
                '& .MuiChip-icon': { fontSize: '14px' }
              }}
            />
          )}
          
          {isOldMeasurement && (
            <Chip 
              icon={<AccessTimeIcon sx={{ fontSize: '12px !important' }} />}
              label={isLowRemaining ? 
                `${daysSinceLastMeasurement}日前測定` : 
                `実測から${daysSinceLastMeasurement}日経過`
              } 
              size="small" 
              color="warning"
              variant={isLowRemaining ? "outlined" : "filled"}
              sx={{ 
                height: isLowRemaining ? 20 : 24,
                fontSize: isLowRemaining ? '0.7rem' : '0.75rem',
                '& .MuiChip-label': { 
                  px: isLowRemaining ? 0.5 : 1, 
                  py: 0 
                },
                '& .MuiChip-icon': { 
                  fontSize: '12px',
                  ml: isLowRemaining ? 0.5 : 1
                }
              }}
            />
          )}
          
          <Chip 
            label="予測機能利用中" 
            size="small" 
            color="success" 
            variant="filled"
          />
        </Box>
      </Box>
    );
  };

  // タンク情報セクションの表示制御
  const renderTankSection = () => {
    if (serviceMode === 'PRICING_ONLY') {
      return (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            <strong>基本モード</strong><br/>
            現在は単価管理のみをご利用中です。タンク管理や予測機能をご希望の場合は、上部の「機能設定」から有効にできます。
          </Typography>
        </Alert>
      );
    }

    if (serviceMode === 'PREDICTION_ONLY') {
      return (
        <Card elevation={2} sx={{ borderRadius: 2, mb: 3, overflow: "hidden" }}>
          <CardContent sx={{ p: 0 }}>
            <Box 
              sx={{ 
                bgcolor: 'info.100', 
                p: 2, 
                borderBottom: 1, 
                borderColor: 'divider',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}
            >
              <Typography variant="subtitle1" fontWeight="medium">
                {buttonConfig.title}
              </Typography>
              {renderHeaderButtons()}
            </Box>
            
            <Box p={3}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Box 
                    sx={{ 
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 1,
                      p: 2,
                      borderRadius: 1,
                      border: 2,
                      borderColor: selectedProduct.measuredDate ? 'info.main' : 'divider',
                      bgcolor: selectedProduct.measuredDate ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
                      boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                    }}
                  >
                    <TimelineIcon 
                      sx={{ 
                        color: 'info.main',
                        fontSize: 28,
                        mt: 0.5
                      }} 
                    />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                        <Typography variant="body2" color="text.secondary" fontWeight="medium">
                          予測残量（容量割合なし）
                        </Typography>
                        <Tooltip title="タンク容量が未設定のため絶対値のみ表示">
                          <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        </Tooltip>
                      </Box>
                      {renderRemainingDisplay()}
                    </Box>
                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box 
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 2,
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider',
                      bgcolor: 'rgba(0, 0, 0, 0.02)',
                      position: 'relative'
                    }}
                  >
                    <WaterDropIcon color="action" />
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          前回測定値（実測）
                        </Typography>
                        
                        {selectedProduct.measuredDate && (
                          <Typography variant="caption" sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5,
                            color: 'text.secondary',
                            bgcolor: 'background.paper',
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            border: '1px dashed',
                            borderColor: 'divider'
                          }}>
                            <CalendarTodayIcon sx={{ fontSize: 12 }} />
                            測定日: {formatDate(selectedProduct.measuredDate)}
                          </Typography>
                        )}
                      </Box>
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                        <Typography variant="body1" fontWeight="medium">
                          {selectedProduct.actualRemaining != null ? 
                            `${selectedProduct.actualRemaining}L` : 
                            "未計測"
                          }
                        </Typography>
                        
                        {selectedProduct.measuredDate && isRecentDate(selectedProduct.measuredDate) && (
                          <Chip 
                            label="最新実測値" 
                            size="small" 
                            color="success"
                            sx={{ ml: 1, height: 20, '& .MuiChip-label': { px: 1, py: 0 } }}
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              </Grid>

              {isPredictionFunctionEnabled && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    消費情報
                  </Typography>
                  
                  <Box 
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 2,
                      borderRadius: 1,
                      border: 1,
                      borderColor: 'divider'
                    }}
                  >
                    <LocalGasStationIcon color="primary" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        ひと月あたりの平均消費量
                      </Typography>
                      <Typography variant="h6" fontWeight="medium">
                        {selectedProduct.initialConsumptionRate != null ? 
                          `${selectedProduct.initialConsumptionRate}L/月` : 
                          "データ不足"
                        }
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      );
    }

    // タンク機能が有効な場合（TANK_MANUAL, PREDICTION_AUTO）
    return (
      <Card elevation={2} sx={{ borderRadius: 2, mb: 3, overflow: "hidden" }}>
        <CardContent sx={{ p: 0 }}>
          <Box 
            sx={{ 
              bgcolor: 'grey.100', 
              p: 2, 
              borderBottom: 1, 
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}
          >
            <Typography variant="subtitle1" fontWeight="medium">
              {buttonConfig.title}
            </Typography>
            {renderHeaderButtons()}
          </Box>
          
          <Box p={3}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={isPredictionFunctionEnabled ? 6 : 12}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                  <Typography variant="subtitle2" color="text.secondary">
                    {isPredictionFunctionEnabled ? 'タンク容量と残量' : 'タンク容量'}
                  </Typography>
                  {isPredictionFunctionEnabled && (
                    <Tooltip title="残量データの説明">
                      <IconButton size="small" color="primary">
                        <HelpOutlineIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
                
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {/* タンク容量 */}
                  <Box 
                    sx={{ 
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      p: 2,
                      borderRadius: 1,
                      border: 1,
                      borderColor: hasCapacity ? 'divider' : 'warning.main',
                      bgcolor: hasCapacity ? 'transparent' : 'rgba(245, 124, 0, 0.04)'
                    }}
                  >
                    <TankIcon color={hasCapacity ? "primary" : "warning"} />
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        タンク容量
                      </Typography>
                      <Typography variant="h6" fontWeight="medium">
                        {hasCapacity ? `${selectedProduct.totalCapacity}L` : "未設定"}
                      </Typography>
                      {!hasCapacity && (
                        <Typography variant="caption" color="warning.main" sx={{ mt: 0.5, display: 'block' }}>
                          設定すると割合表示が利用できます
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* 🚀 修正：補充目安は予測機能が有効な場合のみ表示 */}
                  {selectedProduct.thresholdQuantity && isPredictionFunctionEnabled && (
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 2,
                        borderRadius: 1,
                        border: 1,
                        borderColor: isBelowThreshold(selectedProduct) ? 'error.main' : 'divider',
                        bgcolor: isBelowThreshold(selectedProduct) ? 'rgba(244, 67, 54, 0.04)' : 'transparent'
                      }}
                    >
                      <NotificationsIcon color={isBelowThreshold(selectedProduct) ? "error" : "primary"} />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          補充目安
                        </Typography>
                        <Typography variant="h6" fontWeight="medium">
                          {selectedProduct.thresholdQuantity}L
                        </Typography>
                      </Box>
                      {isBelowThreshold(selectedProduct) && (
                        <Chip 
                          label="補充が必要です" 
                          color="error" 
                          size="small" 
                          variant="outlined"
                          sx={{ ml: 'auto' }}
                        />
                      )}
                    </Box>
                  )}
                  
                  {/* 予測機能有効時のみ残量セクション表示 */}
                  {isPredictionFunctionEnabled && (
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        p: 2,
                        borderRadius: 1,
                        border: 2,
                        borderColor: isLowRemaining ? 
                          'error.main' : (selectedProduct.measuredDate ? 'primary.main' : 'divider'),
                        bgcolor: isLowRemaining ? 
                          'rgba(244, 67, 54, 0.08)' : (selectedProduct.measuredDate ? 'rgba(25, 118, 210, 0.08)' : 'transparent'),
                        boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
                      }}
                    >
                      <OpacityIcon 
                        sx={{ 
                          color: hasCapacity ? getRemainingColor(selectedProduct.predictedRatio) : '#9e9e9e',
                          fontSize: 28,
                          mt: 0.5
                        }} 
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                          <Typography variant="body2" color="text.secondary" fontWeight="medium">
                            現在の残量（予測値）
                          </Typography>
                          <Tooltip title="実測値から消費量に基づいて計算した現時点での推定残量">
                            <InfoIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                          </Tooltip>
                        </Box>
                        {renderRemainingDisplay()}
                      </Box>
                    </Box>
                  )}
                  
                  {/* 実測残量は予測機能有効時のみ表示 */}
                  {isPredictionFunctionEnabled && (
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 2,
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider',
                        bgcolor: 'rgba(0, 0, 0, 0.02)',
                        position: 'relative'
                      }}
                    >
                      <WaterDropIcon color="action" />
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Typography variant="body2" color="text.secondary">
                            前回測定値（実測）
                          </Typography>
                          
                          {selectedProduct.measuredDate && (
                            <Typography variant="caption" sx={{ 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: 0.5,
                              color: 'text.secondary',
                              bgcolor: 'background.paper',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              border: '1px dashed',
                              borderColor: 'divider'
                            }}>
                              <CalendarTodayIcon sx={{ fontSize: 12 }} />
                              測定日: {formatDate(selectedProduct.measuredDate)}
                            </Typography>
                          )}
                        </Box>
                        
                        <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                          <Typography variant="body1" fontWeight="medium">
                            {selectedProduct.actualRemaining != null ? 
                              `${selectedProduct.actualRemaining}L` : 
                              "未計測"
                            }
                          </Typography>
                          
                          {selectedProduct.measuredDate && isRecentDate(selectedProduct.measuredDate) && (
                            <Chip 
                              label="最新実測値" 
                              size="small" 
                              color="success"
                              sx={{ ml: 1, height: 20, '& .MuiChip-label': { px: 1, py: 0 } }}
                            />
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}

                  {/* 予測機能OFF時の控えめな案内（TANK_MANUALモード用） */}
                  {!isPredictionFunctionEnabled && (
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1.5,
                        borderRadius: 1,
                        border: '1px dashed',
                        borderColor: 'divider',
                        bgcolor: 'rgba(0, 0, 0, 0.02)'
                      }}
                    >
                      <TimelineIcon color="disabled" sx={{ fontSize: 20 }} />
                      <Box>
                        <Typography variant="caption" color="text.secondary">
                          予測機能をONにすると残量予測が利用できます
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Box>
              </Grid>
              
              {/* 消費情報は予測機能有効時のみ表示 */}
              {isPredictionFunctionEnabled && (
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    消費情報
                  </Typography>
                  
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 2,
                        borderRadius: 1,
                        border: 1,
                        borderColor: 'divider'
                      }}
                    >
                      <LocalGasStationIcon color="primary" />
                      <Box>
                        <Typography variant="body2" color="text.secondary">
                          ひと月あたりの平均消費量
                        </Typography>
                        <Typography variant="h6" fontWeight="medium">
                          {selectedProduct.initialConsumptionRate != null ? 
                            `${selectedProduct.initialConsumptionRate}L/月` : 
                            "データ不足"
                          }
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return renderTankSection();
};

export default PredictionSection;